// Static asset loader for mesh.bin / mesh.json produced by
// web/export/export_static_assets.py.

export type DType = "f32" | "i32" | "u32" | "u8";

export interface BufferEntry {
  name: string;
  dtype: DType;
  shape: number[];
  offset: number;
  byte_length: number;
}

export interface MeshManifest {
  uv_res: number;
  num_vertices: number;
  num_faces: number;
  buffers: BufferEntry[];
}

export interface MeshAssets {
  manifest: MeshManifest;
  faces: Uint32Array;            // (F, 3)
  idxim: Int32Array;             // (H, W, 3)
  barim: Float32Array;           // (H, W, 3)
  triim: Int32Array;             // (H, W)
  valid: Uint8Array;             // (H, W)
  neutralVertices: Float32Array; // (V, 3)
  frameVertices?: Float32Array;  // (F, V, 3) — flat, 48 frames * V * 3
  numFrames?: number;
}

/**
 * Bytes the manifest expects the blob to hold.
 * Exported for the test — the arithmetic is the whole check.
 */
export function requiredBlobBytes(manifest: MeshManifest): number {
  let needed = 0;
  for (const b of manifest.buffers ?? []) {
    needed = Math.max(needed, b.offset + b.byte_length);
  }
  return needed;
}

/**
 * Fail on a SHORT blob before building any view over it.
 *
 * Without this, a truncated download surfaces from
 * `new Float32Array(blob, offset, len)` as
 * `RangeError: Invalid typed array length: 1769472` — a bare number with no
 * file name, no byte count, and nothing to suggest the bytes simply arrived
 * incomplete. It reads like a code bug and gets debugged like one.
 *
 * Naming the fix matters as much as naming the cause: these blobs are served
 * cache-first (aamAssetCache), so a truncated copy KEEPS being served and a
 * plain reload changes nothing.
 */
export function assertBlobComplete(
  blob: ArrayBuffer,
  manifest: MeshManifest,
  meshBin: string,
): void {
  const needed = requiredBlobBytes(manifest);
  if (blob.byteLength >= needed) return;
  throw new Error(
    `${meshBin} arrived incomplete: got ${blob.byteLength} bytes, manifest needs ${needed} ` +
    `(short by ${needed - blob.byteLength}). This file is cached locally, so reloading will ` +
    `keep failing — use the app menu's "Clear Cache", then reload. If it survives a clear, ` +
    `the download itself is being truncated in transport.`,
  );
}

function view(blob: ArrayBuffer, e: BufferEntry | undefined, meshBin: string, name: string) {
  // A manifest missing an entry would otherwise throw on `e.dtype` — equally
  // cryptic, and a different failure (bad export) needing a different fix.
  if (!e) throw new Error(`${meshBin}: manifest has no "${name}" buffer — the export is incomplete`);
  switch (e.dtype) {
    case "f32": return new Float32Array(blob, e.offset, e.byte_length / 4);
    case "i32": return new Int32Array(blob, e.offset, e.byte_length / 4);
    case "u32": return new Uint32Array(blob, e.offset, e.byte_length / 4);
    case "u8":  return new Uint8Array(blob, e.offset, e.byte_length);
  }
}

// Load one branch's static mesh from arbitrary filenames (mesh_{b}.json/.bin for
// multi-region; mesh.json/.bin for single-region). mesh_{b}.bin carries no
// frame_vertices in the multi-region export (baked verts ship separately / not at
// all — the live GLB drives), so frameVertices is typically undefined here.
export async function loadBranchMesh(
  base: string,
  meshJson: string,
  meshBin: string,
): Promise<MeshAssets> {
  const [manifest, blob] = await Promise.all([
    fetch(`${base}/${meshJson}`).then((r) => r.json() as Promise<MeshManifest>),
    fetch(`${base}/${meshBin}`).then((r) => r.arrayBuffer()),
  ]);
  assertBlobComplete(blob, manifest, meshBin);
  const byName = Object.fromEntries(manifest.buffers.map((b) => [b.name, b]));
  const frameVerts = byName.frame_vertices
    ? (view(blob, byName.frame_vertices, meshBin, "frame_vertices") as Float32Array)
    : undefined;
  return {
    manifest,
    faces: view(blob, byName.faces, meshBin, "faces") as Uint32Array,
    idxim: view(blob, byName.idxim, meshBin, "idxim") as Int32Array,
    barim: view(blob, byName.barim, meshBin, "barim") as Float32Array,
    triim: view(blob, byName.triim, meshBin, "triim") as Int32Array,
    valid: view(blob, byName.valid, meshBin, "valid") as Uint8Array,
    neutralVertices: view(blob, byName.neutral_vertices, meshBin, "neutral_vertices") as Float32Array,
    frameVertices: frameVerts,
    numFrames: byName.frame_vertices?.shape[0],
  };
}

export function loadMeshAssets(base = "/assets/texavatars"): Promise<MeshAssets> {
  return loadBranchMesh(base, "mesh.json", "mesh.bin");
}
